// Platform-agnostic SpeedTrainer API client.
// Configure with base URL, subscription key, and auth callbacks.

export interface ApiTrackPartDto {
  title: string;
  startTime: number;
  stopTime: number;
  bpmOverride?: number | null;
}

export interface ApiTrackLinkDto {
  title: string;
  url: string;
}

export interface ApiTrackSummaryDto {
  id: string;
  name: string;
}

export interface ApiTrackDto {
  id: string;
  name: string;
  youtubeId?: string | null;
  audioFile?: string | null;
  bpm: number;
  defaultPitchShift: number;
  parts: ApiTrackPartDto[];
  links: ApiTrackLinkDto[];
}

export interface CreateTrackBody {
  name: string;
  bpm: number;
  defaultPitchShift: number;
  youtubeId?: string | null;
  audioFile?: string | null;
  parts: ApiTrackPartDto[];
  links: ApiTrackLinkDto[];
}

export interface UpdateTrackBody extends CreateTrackBody {
  id: string;
}

export interface TrackApiServiceConfig {
  apiBaseUrl: string;
  subscriptionKey?: string;
  /** Returns the current auth token, or null/undefined if not logged in. */
  getToken: () => string | null | undefined | Promise<string | null | undefined>;
  /** Called when a request receives a 401 that can't be recovered. */
  onUnauthorized: () => void;
  /** Optional: called before a request to proactively refresh an expired token. */
  silentRefresh?: () => Promise<void>;
  /** Optional: returns whether the stored token is expired. */
  isTokenExpired?: () => boolean | Promise<boolean>;
}

export function createTrackApiService(config: TrackApiServiceConfig) {
  const API_BASE = config.apiBaseUrl.replace(/\/$/, '');
  const SUBSCRIPTION_KEY = config.subscriptionKey ?? '';

  async function buildHeaders(
    extra: Record<string, string> = {}
  ): Promise<Record<string, string>> {
    const token = await config.getToken();
    const headers: Record<string, string> = { ...extra };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (SUBSCRIPTION_KEY) headers['Ocp-Apim-Subscription-Key'] = SUBSCRIPTION_KEY;
    return headers;
  }

  async function authedFetch(
    input: string,
    init: RequestInit = {}
  ): Promise<Response> {
    // Proactive refresh if token is expired
    if (config.isTokenExpired && config.silentRefresh) {
      const expired = await config.isTokenExpired();
      if (expired) {
        try {
          await config.silentRefresh();
        } catch {
          config.onUnauthorized();
          return new Promise<Response>(() => {});
        }
      }
    }

    const doFetch = async (): Promise<Response> =>
      fetch(input, {
        ...init,
        headers: await buildHeaders(init.headers as Record<string, string>),
      });

    const res = await doFetch();

    if (res.status === 401 && config.silentRefresh) {
      try {
        await config.silentRefresh();
        const retry = await doFetch();
        if (retry.status === 401) {
          config.onUnauthorized();
          return new Promise<Response>(() => {});
        }
        return retry;
      } catch {
        config.onUnauthorized();
        return new Promise<Response>(() => {});
      }
    }

    return res;
  }

  async function handleResponse<T>(res: Response): Promise<T> {
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`API error ${res.status}${text ? `: ${text}` : ''}`);
    }
    if (res.status === 204) return undefined as T;
    return res.json() as Promise<T>;
  }

  return {
    getAll(): Promise<ApiTrackDto[]> {
      return authedFetch(`${API_BASE}/api/tracks`).then((r) =>
        handleResponse<ApiTrackDto[]>(r)
      );
    },

    getById(id: string): Promise<ApiTrackDto> {
      return authedFetch(`${API_BASE}/api/tracks/${id}`).then((r) =>
        handleResponse<ApiTrackDto>(r)
      );
    },

    create(body: CreateTrackBody): Promise<ApiTrackDto> {
      return authedFetch(`${API_BASE}/api/tracks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: '', ...body }),
      }).then((r) => handleResponse<ApiTrackDto>(r));
    },

    update(id: string, body: UpdateTrackBody): Promise<ApiTrackDto> {
      return authedFetch(`${API_BASE}/api/tracks/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: '', ...body }),
      }).then((r) => handleResponse<ApiTrackDto>(r));
    },

    delete(id: string): Promise<void> {
      return authedFetch(`${API_BASE}/api/tracks/${id}`, {
        method: 'DELETE',
      }).then((r) => handleResponse<void>(r));
    },
  };
}

export type TrackApiService = ReturnType<typeof createTrackApiService>;
